<template>
    <div>
      <div class="row" v-show="showClassifyWithoutAttributeError" id="attribute-not-set-description">
        <small class="text-danger" :class="{ 'col-md-6': size === 'sm', 'col-md-9': size === 'lg', 'offset-md-2': size === 'sm', 'offset-md-3': size === 'lg' }">
            Należy wybrać atrybut do klasyfikacji.
        </small>
      </div>
      <div class="form-group row">
          <label for="unique-value-attribute" class="control-label col-form-label" :class="{ 'col-md-2': size === 'sm', 'col-md-3': size === 'lg'}">Atrybut</label>
          <div :class="{ 'col-sm-6': size === 'sm', 'col-sm-7': size === 'lg'}">
              <b-form-select v-model="selectedAttribute" :options="attributesToSelect" class="mb-3" id="unique-value-attribute" :class="{ 'is-invalid': showClassifyWithoutAttributeError }" aria-required="true" :aria-invalid="showClassifyWithoutAttributeError" :aria-describedby="showClassifyWithoutAttributeError ? 'attribute-not-set-description' : ''"></b-form-select>                 
          </div>
          <div class="col-sm-1">
            <b-btn variant="primary" v-on:click="classify">Klasyfikuj</b-btn>
          </div>
      </div>
      <div v-if="selectedAttributeManyValues === true">
        <b-alert show variant="warning"><strong>Uwaga.</strong> Wybrany atrybut posiada wiele unikalnych wartości.</b-alert>
      </div>
      <div v-if="selectedAttributeTooManyValues === true">
        <b-alert show variant="warning"><strong>Uwaga.</strong> Wybrany atrybut posiada zbyt wiele unikalnych wartości. Wartości zostały ograniczone do 1024.</b-alert>
      </div>
      <div class="table-responsive" v-if="elementsCount !== 0">
        <table class="table table-striped">
          <thead>
              <tr>
                <th>Wartość</th>
                <th>Kolor wypełnienia</th>
                <th>Deseń</th>
                <th>Styl obrysu</th>
                <th>Szerokość obrysu</th>
                <th>Kolor obrysu</th>
                <th>Akcje</th>
              </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in attributeUniqueValues" :key="row.name + index">
              <td>{{ getNameFromAttributes(row.name) }}</td>
              <td><color-picker v-model="row.fillColor" size="sm"/></td>
              <td>
                <span v-show="row.styleDataErrors.incorrectFileFormat" :id="'incorrect-file-format-description' + index">
                  <small class="text-danger">
                    Wybrano nieprawidłowy format pliku. Dopuszczalny format pliku to png.
                  </small>
                </span>
                <b-form-file v-model="row.designFileBuffer" accept="image/png" size="sm" :class="{ 'is-invalid': row.styleDataErrors.incorrectFileFormat }" :aria-invalid="row.styleDataErrors.incorrectFileFormat" :aria-describedby="row.styleDataErrors.incorrectFileFormat ? 'incorrect-file-forma-description-' + index : ''" v-on:change="fileChanged($event, row)"></b-form-file>
                <small class="form-text text-muted" v-if="row.designFileSaved">
                  Używany <a :href="row.design" download="obraz.png">plik</a>. <b-btn variant="outline-primary" size="sm" @click="setFileValue(row, null, false)">Usuń</b-btn>
                </small>
              </td>
              <td><b-form-select v-model="row.strokeStyle" :options="strokeStylesList" size="sm"></b-form-select></td>
              <td>
                <span v-show="row.styleDataErrors.strokeSizeEmpty" :id="'stroke-size-empty-description-' + index">
                  <small class="text-danger">
                      Należy podać szerokość obrysu.
                  </small>
                </span>
                <span v-show="row.styleDataErrors.strokeSizeNotInRange" :id="'stroke-size-range-description-' + index">
                  <small class="text-danger">
                      Szerokość obrysu powinna zawierać się w przedziale od 1 do 255.
                  </small>
                </span>
                <b-form-input v-model="row.strokeSize" type="number" min="1" max="255" size="sm" class="form-control" :class="{ 'is-invalid': row.styleDataErrors.strokeSizeEmpty || row.styleDataErrors.strokeSizeNotInRange }" :aria-required="row.strokeStyle !== null" :aria-invalid="row.styleDataErrors.strokeSizeEmpty || row.styleDataErrors.strokeSizeNotInRange" :aria-describedby="row.styleDataErrors.strokeSizeEmpty ? 'stroke-size-empty-description-' + index : (row.styleDataErrors.strokeSizeNotInRange ? 'stroke-size-range-description-' + index : '')"></b-form-input>
              </td>
              <td><color-picker v-model="row.strokeColor" size="sm"/></td>
              <td class="text-center"><a href="#" title="Usuń" @click="deleteRow(row)"><span class="oi oi-trash"></span></a></td>
            </tr>
          </tbody>
        </table>
      </div>
      <b-row align-v="center" v-if="elementsCount > 20">
        <b-col cols="4">
          Elementów na stronie: {{ elementsOnPageStart }} - {{ elementsOnPageEnd }} / {{ elementsCount }}
        </b-col>
        <b-col cols="8">
          <b-pagination align="right" :total-rows="elementsCount" :per-page="elementsPerPage" @input="changePage">
          </b-pagination>
        </b-col>
      </b-row>
      <div v-if="selectedUniqueValueAttribute === null">
        <b-alert show variant="info"><strong>Informacja.</strong> Nie wybrano żadnego atrybutu.</b-alert>
      </div>
      <div v-if="selectedAttributeEmptyValues === true">
        <b-alert show variant="warning"><strong>Uwaga.</strong> Wybrany atrybut nie ma unikalnych wartości.</b-alert>
      </div>
      <error-modal ref="errorModal"></error-modal>
      <loading-overlay :active.sync="loading" :is-full-page="true"></loading-overlay>
    </div>
</template>

<script>
import ColorPicker from '../../ColorPicker.vue';
import ErrorModal from "../../ErrorModal.vue";
import { Api as ApiStyle } from "../../../api/api-style.js";
import { getTimestamp, getRandomColor } from "../../../api/HelperFunctions";
import { getBase64Image } from '../../../api/HelperFunctions';
import LayerStyleStore from "../store/LayerStyleStore";
import LoadingOverlay from "vue-loading-overlay";
import VueScrollTo from 'vue-scrollto';

export default {
    name: "FormLayerStylePolygonUniqueValue",
    props: {
      attributes: Array,
      layerId: String,
      size: String
    },
    data () {
      return {
        attributesToSelect: [],
        selectedAttribute: null,
        selectedUniqueValueAttribute: null,
        selectedAttributeEmptyValues: false,
        selectedAttributeTooManyValues: false,
        attributeUniqueValues: [],
        strokeStylesList: [
          { text: "Wybierz", value: null },
          { text: "Kreskowany", value: [4, 4] },
          { text: "Kropkowany", value: [1, 1] },
          { text: "Ciągły", value: [1] }
        ],
        lastRequestTimestamp: null,
        showClassifyWithoutAttributeError: false,
        currentPage: 1,
        elementsPerPage: 20,
        elementsCount: 0,
        loading: false
      }
    },
    computed: {
      elementsOnPageStart() {
        return ((this.currentPage - 1) * this.elementsPerPage) + (this.attributeUniqueValues.length ? 1 : 0);
      },
      elementsOnPageEnd() {
        return ((this.currentPage -1) * this.elementsPerPage) + this.attributeUniqueValues.length;
      },
      selectedAttributeManyValues() {
        return !this.selectedAttributeTooManyValues && this.elementsCount > 128;
      }
    },
    mounted() {
      this.attributesToSelect = [];
      this.attributesToSelect.push({ text: "Wybierz", value: null });
      if (this.attributes !== null) {
        for (let i = 0; i < this.attributes.length; ++i) {
          this.attributesToSelect.push({
            text: this.attributes[i].name ? this.attributes[i].name : this.attributes[i].description,
            value: this.attributes[i].key
          });
        }
      }
      //Pobranie danych z obiektu przechowującego style.
      let store = new LayerStyleStore();
      const initialValue = store.getPolygonUniqueValue();
      if (initialValue != null && initialValue.reguly !== undefined && initialValue.reguly.length !== 0) {
        let buffer = store.getPolygonUniqueValueBuffer();
        for (let i = 0; i < initialValue.reguly.length; ++i) {
          if (i === 0) {
            this.selectedAttribute = initialValue.reguly[i].filtr.atrybut;
            this.selectedUniqueValueAttribute = initialValue.reguly[i].filtr.atrybut;
          }
          buffer.push({
            name: initialValue.reguly[i].filtr.wartosc,
            fillColor: initialValue.reguly[i].sygnatura.wypelnienie.kolor,
            design: initialValue.reguly[i].sygnatura.wypelnienie.obraz,
            designFileSaved: initialValue.reguly[i].sygnatura.wypelnienie.obraz != null && initialValue.reguly[i].sygnatura.wypelnienie.obraz.length > 0,
            designFileBuffer: null,
            strokeStyle: initialValue.reguly[i].sygnatura.obrys.kreskowanie,
            strokeSize: initialValue.reguly[i].sygnatura.obrys.szerokosc,
            strokeColor: initialValue.reguly[i].sygnatura.obrys.kolor,
            styleDataErrors: {
              strokeSizeEmpty: false,
              strokeSizeNotInRange: false,
              incorrectFileFormat: false
            }
          });
        }
      }
      this.setUniqueValuesReactive();
      this.setTooltips();
    },
    updated() {
      this.setSortTooltips();
    },
    watch:{
      selectedUniqueValueAttribute: function(value) {
          if(value===null){
            VueScrollTo.scrollTo({container:'body',easing: "ease"});
          }
        },
      selectedAttributeEmptyValues: function(value) {
          if(value){
            VueScrollTo.scrollTo({container:'body',easing: "ease"});
          }
        }
    },
    methods: {
      classify() {
        if (this.selectedAttribute !== null) {
          this.loading = true;
          this.showClassifyWithoutAttributeError = false;
          this.selectedAttributeTooManyValues = false;
          var vm = this;
          this.selectedAttributeEmptyValues = false;
          this.selectedUniqueValueAttribute = this.selectedAttribute;
          let store = new LayerStyleStore();
          this.lastRequestTimestamp = getTimestamp();
          var api = new ApiStyle();
          api
            .getWartosci({
              control: vm.lastRequestTimestamp,
              field: vm.selectedUniqueValueAttribute,
              layer: vm.layerId
            })
            .then(function(response) {
              vm.showError(false, null);
              if (response.body.control === vm.lastRequestTimestamp.toString()) {
                if(response.body.status === 'overflow'){
                vm.selectedAttributeTooManyValues = true;
              }
                let buffer = store.getPolygonUniqueValueBuffer();
                buffer.clear();
                if (response.body.unique_values.length !== 0) {
                  for (var i = 0; i < response.body.unique_values.length; ++i) {
                    var color = getRandomColor();
                    buffer.push({
                      name: response.body.unique_values[i],
                      fillColor: color,
                      design: null,
                      designFileSaved: false,
                      designFileBuffer: null,
                      strokeStyle: [1],
                      strokeSize: 3,
                      strokeColor: color,
                      styleDataErrors: {
                        strokeSizeEmpty: false,
                        strokeSizeNotInRange: false,
                        incorrectFileFormat: false
                      }
                    });
                  }
                } else {
                  vm.selectedAttributeEmptyValues = true;
                }
                vm.setUniqueValuesReactive();
              }
            })
            .catch(function(err) {
              vm.showError(true, 'Podczas klasyfikacji wystąpił błąd. Spróbuj ponownie później.');
            }).finally(function(){
              setTimeout(() => { vm.loading = false; }, 0);
            });
        } else {
          this.showClassifyWithoutAttributeError = true;
        }
      },
      deleteRow(entry) {
        if (this.attributeUniqueValues != null) {
          let store = new LayerStyleStore();
          let buffer = store.getPolygonUniqueValueBuffer();
          buffer.remove(entry);
          this.setUniqueValuesReactive();
        }
      },
      updateValue() {
        let emitValue = {
          reguly: []
        };
        let store = new LayerStyleStore();
        let buffer = store.getPolygonUniqueValueBuffer();
        for (let i = 0; i < buffer.count(); ++i) {
          const bufferItem = buffer.get(i);
          emitValue.reguly.push({
            filtr: {
              atrybut: this.selectedUniqueValueAttribute,
              wartosc: bufferItem.name
            },
            sygnatura: {
              obrys: {
                kolor: bufferItem.strokeColor,
                kreskowanie: bufferItem.strokeStyle,
                szerokosc: bufferItem.strokeSize ? parseInt(bufferItem.strokeSize) : null
              },
              wypelnienie: {
                kolor: bufferItem.fillColor,
                obraz: bufferItem.design
              }
            }
          });
        }
        store.setPolygonUniqueValue(emitValue);
        this.$emit('updated');
      },
      setUniqueValuesReactive() {
        let store = new LayerStyleStore();
        let buffer = store.getPolygonUniqueValueBuffer();
        if(buffer.any()) {
          const currentIndex = (this.currentPage - 1) * this.elementsPerPage;
          this.attributeUniqueValues = buffer.get(currentIndex, currentIndex + this.elementsPerPage);
          this.elementsCount = buffer.count();
        } else {
          this.attributeUniqueValues = [];
          this.elementsCount = 0;
        }
      },
      changePage(value) {
        this.currentPage = value;
        this.setUniqueValuesReactive();
      },
      validate() {
        this.updateValue();
        var vm = this;
        let store = new LayerStyleStore();
        let buffer = store.getPolygonUniqueValueBuffer();
        let bufferArray = buffer.get();
        bufferArray.forEach(x =>
          Object.keys(x.styleDataErrors).forEach(
            y => (x.styleDataErrors[y] = false)
          )
        );
        this.showClassifyWithoutAttributeError = false;
        if(buffer.count() !== 0){
          for (let i = 0; i < buffer.count(); ++i) {
            const bufferItem = buffer.get(i);
            if(bufferItem.designFileBuffer !== undefined && bufferItem.designFileBuffer !== null && bufferItem.designFileBuffer.type !== 'image/png') {
              bufferItem.styleDataErrors.incorrectFileFormat = true;
            }
            if (bufferItem.strokeStyle !== null) {
              if (bufferItem.strokeSize === "") {
                bufferItem.styleDataErrors.strokeSizeEmpty = true;
              } else if (parseInt(bufferItem.strokeSize) < 1 || parseInt(bufferItem.strokeSize) > 255) {
                bufferItem.styleDataErrors.strokeSizeNotInRange = true;
              }
            }
            buffer.set(bufferItem, i);
          }
          this.setUniqueValuesReactive();
        } else {
          this.showClassifyWithoutAttributeError = true;
        }
        return this.showClassifyWithoutAttributeError || bufferArray.some(x =>
          Object.keys(x.styleDataErrors).some(y => x.styleDataErrors[y] === true)
        );
      },
      fileChanged(event, row) {
        let files = event.target.files;
        let vm = this;
        if (files.length) {
          if(files[0].type !== 'image/png'){
            this.setFileValue(row, null, true);
          } else {
            getBase64Image(files[0]).then(result => {
              vm.setFileValue(row, result, false);
            }, error => {
              vm.setFileValue(row, null, true);
            });
          }
        } else {
          this.setFileValue(row, null, false);
        }
      },
      setFileValue(row, file, error){
        row.designFileSaved = false;
        row.design = file;
        row.styleDataErrors.incorrectFileFormat = error;
      },
      showError(show, errorMessage) {
        if(this.size === 'lg' || show === false){
          this.$emit('errorMessage', { show: show, errorMessage: errorMessage });
        } else {
          this.$refs.errorModal.show(errorMessage);
        }
      },
      setTooltips() {
        var paginationButtons = Array.from(document.getElementsByClassName("page-item"));

        var firsts = paginationButtons.filter(pb => pb.innerText.indexOf("«") != -1);
        var previous = paginationButtons.filter(pb => pb.innerText.indexOf("‹") != -1);
        var nexts = paginationButtons.filter(pb => pb.innerText.indexOf("›") != -1);
        var lasts = paginationButtons.filter(pb => pb.innerText.indexOf("»") != -1);

        firsts.forEach(item => item.title = "Pierwsza");
        previous.forEach(item => item.title = "Poprzednia");
        nexts.forEach(item => item.title = "Następna");
        lasts.forEach(item => item.title = "Ostatnia");
      },
      setSortTooltips() {
        var sortButtons = Array.from(document.getElementsByClassName("sorting"));
        sortButtons.forEach(item => item.title = "Sortuj");
      },
      getNameFromAttributes(name) {
        if(this.attributes && this.selectedAttribute && this.selectedAttribute !== "") {
          const attribute = this.attributes.find(x => {
            return x.key.toLowerCase() === this.selectedAttribute.toLowerCase();
          })
          if(attribute && attribute.dictionary) {
            const translatedName = attribute.dictionary.find(x => {
              return x.litera === name;
            });
            if(translatedName && translatedName.wartosc) {
              return translatedName.wartosc;
            }
          }
        }
        return name;
      }
    },
    components: {
      ColorPicker,
      ErrorModal,
      LoadingOverlay,
      VueScrollTo
    }
  }
</script>